import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import UserConfigCard from "../components/UserConfigCard";

import {
  LogOut,
  Settings,
  Users,
  TrendingUp,
  DollarSign,
  Plus,
  Edit,
  Trash,
  Check,
  X,
  RefreshCw,
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  Home,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  ShoppingCart,
  TrendingDown,
  BarChart3,
  Clock3,
  BadgeDollarSign,
  CircleDollarSign,
} from "lucide-react";

// ===== helpers =====
function n(v, fallback = 0) {
  const x = Number(v);
  return Number.isFinite(x) ? x : fallback;
}

function formatMoney(value) {
  return n(value, 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumber(value, digits = 2) {
  const x = Number(value);
  if (!Number.isFinite(x)) return "--";
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("pt-BR");
}

function formatTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("pt-BR");
}

// parse robusto (ISO / "YYYY-MM-DD HH:mm:ss" / "DD/MM/YYYY HH:mm:ss")
function parseTimestampToMs(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts > 1e12 ? ts : ts * 1000;
  const s = String(ts).trim();
  if (!s) return null;

  // ISO / YYYY-MM-DD HH:mm:ss
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const iso = s.includes("T") ? s : s.replace(" ", "T");
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
  }

  // BR: DD/MM/YYYY ...
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) {
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    const HH = Number(m[4] || 0);
    const MI = Number(m[5] || 0);
    const SS = Number(m[6] || 0);
    const ms = new Date(yyyy, mm - 1, dd, HH, MI, SS).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

function ageSecondsFromTs(ts) {
  const ms = parseTimestampToMs(ts);
  if (!Number.isFinite(ms) || ms === null) return null;
  const s = Math.floor((Date.now() - ms) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}

export default function AdminDashboard({ user, onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [activeAdminTab, setActiveAdminTab] = useState("home");

  // ===== Lista compacta de usuários =====
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersQuery, setUsersQuery] = useState("");
  const [usersSearchInput, setUsersSearchInput] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [deposits, setDeposits] = useState([]);

  // Saques
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawStatus, setWithdrawStatus] = useState("pending"); // pending | paid | rejected | all

  // Cotações (bolsa + dólar)
  const [market, setMarket] = useState(null);

  // Histórico / auditoria de negociações
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesPeriod, setTradesPeriod] = useState("30d");
  const [tradesStatus, setTradesStatus] = useState("all");
  const [tradesSide, setTradesSide] = useState("all");
  const [tradesPage, setTradesPage] = useState(1);
  const [tradesTotalPages, setTradesTotalPages] = useState(1);
  const [tradesSummary, setTradesSummary] = useState({
    volume: 0,
    client_pl: 0,
    house_gross_pl: 0,
    partner_commission: 0,
    house_net_pl: 0,
  });

  // UX “profissional”
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshMs, setRefreshMs] = useState(15000);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [lastRefreshOk, setLastRefreshOk] = useState(true);
  const [refreshingNow, setRefreshingNow] = useState(false);

  const [editingContract, setEditingContract] = useState(null);
  const [newContract, setNewContract] = useState({
    symbol: "",
    name: "",
    spread: 0,
    active: true,
  });

  // Headers auth (caso axios.defaults não esteja setado)
  const authConfig = useMemo(() => {
    const t = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
  }, []);

  // ===== fetchers (silenciosos por padrão) =====
  const fetchDashboard = async () => {
    const r = await axios.get(`${API}/admin/dashboard`, authConfig);
    const data = r.data || {};
    setDashboard({
      ...data,
      total_users: data.total_users ?? 0,
      total_balance: data.total_balance ?? 0,
      house_profit: data.house_profit ?? 0,
      total_deposit_amount: data.total_deposit_amount ?? 0,
      brl_available: data.brl_available ?? 0,
      total_balance: data.total_balance ?? data.brl_available ?? 0,
      open_buy_total: data.open_buy_total ?? 0,
      open_sell_total: data.open_sell_total ?? 0,
      open_orders_total: data.open_orders_total ?? 0,
      open_buy_count: data.open_buy_count ?? data.buy_open_count ?? data.open_buys_count ?? 0,
      open_sell_count: data.open_sell_count ?? data.sell_open_count ?? data.open_sells_count ?? 0,
      open_orders_count: data.open_orders_count ?? data.orders_open_count ?? 0,
      house_profit_today: data.house_realized_today ?? data.house_profit_today ?? data.today_house_profit ?? null,
      portfolio_total: data.portfolio_total ?? data.total_portfolio ?? data.patrimony_total ?? null,

      risk_market_ok: Boolean(data.risk_market_ok),
      risk_fair_price: data.risk_fair_price ?? 0,
      open_buy_qty: data.open_buy_qty ?? 0,
      open_sell_qty: data.open_sell_qty ?? 0,
      net_customer_qty: data.net_customer_qty ?? 0,
      net_exposure_brl: data.net_exposure_brl ?? 0,
      hedge_side: data.hedge_side ?? "flat",
      hedge_qty_sacks: data.hedge_qty_sacks ?? 0,
      hedge_notional_brl: data.hedge_notional_brl ?? 0,

      client_open_pl: data.client_open_pl ?? 0,
      house_open_mtm: data.house_open_mtm ?? 0,
      client_realized_pl: data.client_realized_pl ?? 0,
      house_realized_gross: data.house_realized_gross ?? 0,
      house_realized_today: data.house_realized_today ?? 0,
      house_gross_including_open: data.house_gross_including_open ?? 0,
      house_net_result: data.house_net_result ?? data.house_profit ?? 0,

      spread_revenue_open: data.spread_revenue_open ?? 0,
      spread_revenue_closed: data.spread_revenue_closed ?? 0,
      spread_revenue_total: data.spread_revenue_total ?? 0,

      partner_commissions_total: data.partner_commissions_total ?? 0,
      partner_commissions_available: data.partner_commissions_available ?? 0,
      partner_commissions_paid: data.partner_commissions_paid ?? 0,

      risk_shock_1pct_brl: data.risk_shock_1pct_brl ?? 0,
      risk_shock_5pct_brl: data.risk_shock_5pct_brl ?? 0,
      risk_shock_10pct_brl: data.risk_shock_10pct_brl ?? 0,

      // Ordens abertas calculadas pelo admin.php atual do servidor
      open_positions_live: Array.isArray(data.open_positions_live) ? data.open_positions_live : [],

      users: Array.isArray(data.users) ? data.users : [],
    });
  };

  const fetchAdminUsers = async ({ page = usersPage, q = usersQuery } = {}) => {
    setUsersLoading(true);

    try {
      const r = await axios.get(`${API}/admin/users`, {
        params: {
          page,
          limit: 10,
          q,
        },
      });

      const data = r?.data ?? {};

      setAdminUsers(Array.isArray(data.rows) ? data.rows : []);
      setUsersPage(Number(data.page) || 1);
      setUsersTotal(Number(data.total) || 0);
      setUsersTotalPages(Math.max(1, Number(data.total_pages) || 1));
    } catch (error) {
      console.error("[ADMIN USERS]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail || "Erro ao carregar usuários."
      );
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUsersSearch = (e) => {
    e?.preventDefault?.();

    const q = String(usersSearchInput || "").trim();
    setUsersQuery(q);
    setUsersPage(1);
    setExpandedUserId(null);
    fetchAdminUsers({ page: 1, q });
  };

  const handleUsersClearSearch = () => {
    setUsersSearchInput("");
    setUsersQuery("");
    setUsersPage(1);
    setExpandedUserId(null);
    fetchAdminUsers({ page: 1, q: "" });
  };

  const goToUsersPage = (page) => {
    const next = Math.min(
      Math.max(1, Number(page) || 1),
      Math.max(1, usersTotalPages)
    );

    if (next === usersPage || usersLoading) return;

    setUsersPage(next);
    setExpandedUserId(null);
    fetchAdminUsers({ page: next, q: usersQuery });
  };


  const fetchSettings = async () => {
    const r = await axios.get(`${API}/admin/settings`, authConfig);
    setSettings(r.data || {});
  };

  const fetchContracts = async () => {
    const r = await axios.get(`${API}/admin/contracts`, authConfig);
    setContracts(Array.isArray(r.data) ? r.data : []);
  };

  const fetchDeposits = async () => {
    const r = await axios.get(`${API}/admin/deposits`, authConfig);
    setDeposits(Array.isArray(r.data) ? r.data : []);
  };

  const fetchWithdrawals = async (status = withdrawStatus) => {
    const r = await axios.get(
      `${API}/admin/withdrawals?status=${encodeURIComponent(status)}`,
      authConfig
    );
    const rows = r.data?.rows;
    setWithdrawals(Array.isArray(rows) ? rows : []);
    setWithdrawStatus(status);
  };

  const fetchMarket = async () => {
    const r = await axios.get(`${API}/market/data`, authConfig);
    setMarket(r.data || null);
  };

  const fetchTrades = async ({
    page = tradesPage,
    period = tradesPeriod,
    status = tradesStatus,
    side = tradesSide,
  } = {}) => {
    setTradesLoading(true);

    try {
      const r = await axios.get(`${API}/admin/trades`, {
        ...authConfig,
        params: {
          page,
          limit: 20,
          period,
          status,
          side,
        },
      });

      const data = r?.data || {};
      setTrades(Array.isArray(data.rows) ? data.rows : []);
      setTradesPage(Number(data.page) || 1);
      setTradesTotalPages(Math.max(1, Number(data.total_pages) || 1));
      setTradesSummary({
        volume: n(data?.summary?.volume),
        client_pl: n(data?.summary?.client_pl),
        house_gross_pl: n(data?.summary?.house_gross_pl),
        partner_commission: n(data?.summary?.partner_commission),
        house_net_pl: n(data?.summary?.house_net_pl),
      });
    } catch (error) {
      console.error("[ADMIN TRADES]", error?.response?.data || error);
      toast.error(
        error?.response?.data?.detail || "Erro ao carregar negociações."
      );
    } finally {
      setTradesLoading(false);
    }
  };

  // ===== refresh all (com controle de erro) =====
  const refreshNow = async (opts = { toastOnError: false }) => {
    setRefreshingNow(true);
    try {
      const results = await Promise.allSettled([
        fetchDashboard(),
        fetchDeposits(),
        fetchWithdrawals(withdrawStatus),
        fetchMarket(),
      ]);

      const ok = results.every((x) => x.status === "fulfilled");
      setLastRefreshOk(ok);
      setLastRefreshAt(new Date());

      if (!ok && opts.toastOnError) {
        toast.error("Falha ao atualizar alguns dados. Verifique o servidor.");
      }
    } catch (e) {
      setLastRefreshOk(false);
      setLastRefreshAt(new Date());
      if (opts.toastOnError) toast.error("Erro ao atualizar dados.");
    } finally {
      setRefreshingNow(false);
    }
  };

  // ===== init =====
  useEffect(() => {
    (async () => {
      try {
        await fetchDashboard();
        await fetchSettings();
        await fetchContracts();
        await fetchDeposits();
        await fetchWithdrawals("pending");
        await fetchMarket();
        setLastRefreshOk(true);
        setLastRefreshAt(new Date());
      } catch (error) {
        console.error("[AdminDashboard] init error:", error?.response?.data || error);
        setLastRefreshOk(false);
        setLastRefreshAt(new Date());
        toast.error("Erro ao carregar painel admin");
      }
    })();
  }, []);

  // ===== usuários: carga inicial =====
  useEffect(() => {
    fetchAdminUsers({ page: 1, q: "" });
  }, []);

  useEffect(() => {
    if (activeAdminTab !== "trades") return;
    fetchTrades({
      page: tradesPage,
      period: tradesPeriod,
      status: tradesStatus,
      side: tradesSide,
    });
  }, [activeAdminTab]);

  // ===== auto refresh =====
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      refreshNow({ toastOnError: false });
    }, refreshMs);

    return () => clearInterval(id);
  }, [autoRefresh, refreshMs, withdrawStatus]);

  // ===== actions =====
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings, authConfig);
      toast.success("Configurações salvas!");
    } catch (error) {
      console.error("[AdminDashboard] save settings:", error?.response?.data || error);
      toast.error("Erro ao salvar configurações");
    }
  };

  const handleCreateContract = async () => {
    try {
      const payload = { ...newContract, spread: Number(newContract.spread ?? 0) };
      await axios.post(`${API}/admin/contracts`, payload, authConfig);
      toast.success("Contrato criado!");
      setNewContract({ symbol: "", name: "", spread: 0, active: true });
      fetchContracts();
    } catch (error) {
      console.error("[AdminDashboard] create contract:", error?.response?.data || error);
      toast.error("Erro ao criar contrato");
    }
  };

  const handleUpdateContract = async (contract) => {
    try {
      const payload = { ...contract, spread: Number(contract.spread ?? 0) };
      await axios.put(`${API}/admin/contracts/${contract.id}`, payload, authConfig);
      toast.success("Contrato atualizado!");
      setEditingContract(null);
      fetchContracts();
    } catch (error) {
      console.error("[AdminDashboard] update contract:", error?.response?.data || error);
      toast.error("Erro ao atualizar contrato");
    }
  };

  const handleDeleteContract = async (id) => {
    if (!window.confirm("Tem certeza que deseja deletar este contrato?")) return;
    try {
      await axios.delete(`${API}/admin/contracts/${id}`, authConfig);
      toast.success("Contrato deletado!");
      fetchContracts();
    } catch (error) {
      console.error("[AdminDashboard] delete contract:", error?.response?.data || error);
      toast.error("Erro ao deletar contrato");
    }
  };

  const handleConfirmDeposit = async (depositId) => {
    try {
      await axios.post(`${API}/deposit/${depositId}/confirm`, {}, authConfig);
      toast.success("Depósito confirmado!");
      fetchDeposits();
      fetchDashboard();
    } catch (error) {
      console.error("[AdminDashboard] confirm deposit:", error?.response?.data || error);
      toast.error("Erro ao confirmar depósito");
    }
  };

  const handleConfirmWithdrawal = async (withdrawalId) => {
    if (!window.confirm("Confirmar este saque como PAGO?")) return;
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/pay`, {}, authConfig);
      toast.success("Saque confirmado (pago)!");
      fetchWithdrawals(withdrawStatus);
      fetchDashboard();
    } catch (error) {
      console.error("[AdminDashboard] confirm withdrawal:", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao confirmar saque");
    }
  };

  const handleRejectWithdrawal = async (withdrawalId) => {
    if (!window.confirm("RECUSAR este saque e DEVOLVER o saldo ao usuário?")) return;
    const reason = window.prompt("Motivo (opcional):") || "";
    try {
      await axios.post(`${API}/admin/withdrawals/${withdrawalId}/reject`, { reason }, authConfig);
      toast.success("Saque recusado e saldo devolvido!");
      fetchWithdrawals(withdrawStatus);
      fetchDashboard();
    } catch (error) {
      console.error("[AdminDashboard] reject withdrawal:", error?.response?.data || error);
      toast.error(error?.response?.data?.detail || "Erro ao recusar saque");
    }
  };

  const pendingDeposits = (deposits || []).filter((d) => d && d.status === "pending");
  const pendingWithdrawals = (withdrawals || []).filter((w) => w && w.status === "pending");

  // ===== market display =====
  const mktOk = !!(market && market.ok);
  const kc = mktOk ? n(market.kc_cents_per_lb, null) : null;
  const fx = mktOk ? n(market.usdtbrl, null) : null;
  const saca = mktOk ? n(market.saca_brl ?? market.brl_per_saca ?? market.price, null) : null;

  // ✅ cache: primeiro usa cache_age_seconds, se não tiver calcula via timestamps do payload
  const cacheKC =
    mktOk
      ? (
          (Number.isFinite(Number(market?.cache_age_seconds?.kc)) ? Number(market.cache_age_seconds.kc) : null) ??
          ageSecondsFromTs(market?.kc_saved_at || market?.kc_source_ts || market?.kc_data_hora)
        )
      : null;

  const cacheFX =
    mktOk
      ? (
          (Number.isFinite(Number(market?.cache_age_seconds?.fx)) ? Number(market.cache_age_seconds.fx) : null) ??
          (Number.isFinite(Number(market?.cache_age_seconds?.dol)) ? Number(market.cache_age_seconds.dol) : null) ??
          ageSecondsFromTs(market?.fx_saved_at || market?.fx_source_ts || market?.fx_data_hora)
        )
      : null;

  const pendingDepositAmount = pendingDeposits.reduce((sum, d) => sum + n(d?.amount, 0), 0);
  const pendingWithdrawalAmount = pendingWithdrawals.reduce((sum, w) => sum + n(w?.amount, 0), 0);

  const marketOpen = mktOk ? n(market?.open ?? market?.kc_open ?? market?.raw?.kc?.open, null) : null;
  const marketHigh = mktOk ? n(market?.high ?? market?.kc_high ?? market?.raw?.kc?.high, null) : null;
  const marketLow = mktOk ? n(market?.low ?? market?.kc_low ?? market?.raw?.kc?.low, null) : null;
  const marketClose = mktOk ? n(market?.close ?? market?.previous_close ?? market?.prev_close ?? market?.kc_close ?? market?.raw?.kc?.close ?? market?.raw?.kc?.previous_close, null) : null;
  const marketBid = mktOk ? n(market?.bid ?? market?.kc_bid ?? market?.raw?.kc?.bid, null) : null;
  const marketAsk = mktOk ? n(market?.ask ?? market?.kc_ask ?? market?.raw?.kc?.ask, null) : null;
  const marketChangePct = mktOk ? n(market?.change_pct ?? market?.kc_change_pct ?? market?.raw?.kc?.change_pct, 0) : 0;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-3 py-3 sm:px-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
              <Settings className="w-6 h-6 text-slate-900" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                Painel Admin
              </h1>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0 md:gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Activity className="w-4 h-4" />
              <span className="text-slate-400">Auto</span>
              <Switch checked={autoRefresh} onCheckedChange={(v) => setAutoRefresh(!!v)} />
            </div>

            <select
              value={refreshMs}
              onChange={(e) => setRefreshMs(Number(e.target.value))}
              className="px-2 py-2 bg-slate-900 border border-slate-700 rounded-md text-white text-sm"
              title="Intervalo"
            >
              <option value={10000}>10s</option>
              <option value={15000}>15s</option>
              <option value={30000}>30s</option>
              <option value={60000}>60s</option>
            </select>

            <Button
              variant="outline"
              onClick={() => refreshNow({ toastOnError: true })}
              className="border-slate-700"
              disabled={refreshingNow}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshingNow ? "animate-spin" : ""}`} />
              Atualizar agora
            </Button>

            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${lastRefreshOk ? "bg-emerald-400" : "bg-red-400"}`} />
              Última: {formatTime(lastRefreshAt)}
            </div>

            <Button variant="outline" onClick={onLogout} className="border-slate-700">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 sm:py-6">
        <div className="sticky top-[88px] z-40 -mx-3 mb-5 border-y border-slate-800 bg-slate-950/95 px-3 py-2 backdrop-blur sm:top-[92px] sm:mx-0 sm:rounded-2xl sm:border">
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[
              ["home", "Início", Home],
              ["users", "Usuários", Users],
              ["deposits", "Depósitos", ArrowDownToLine],
              ["withdrawals", "Saques", ArrowUpFromLine],
              ["trades", "Negociações", Activity],
              ["contracts", "Contratos", BarChart3],
              ["settings", "Config.", Settings],
            ].map(([value, label, Icon]) => (
              <button key={value} type="button" onClick={() => setActiveAdminTab(value)}
                className={`flex min-w-max items-center gap-2 rounded-xl border px-4 py-3 text-sm font-black transition active:scale-95 ${
                  activeAdminTab === value ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-300" : "border-slate-800 bg-slate-900 text-slate-400 hover:text-white"
                }`}>
                <Icon className="h-4 w-4" />
                {label}
                {value === "deposits" && pendingDeposits.length > 0 && <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] text-slate-950">{pendingDeposits.length}</span>}
                {value === "withdrawals" && pendingWithdrawals.length > 0 && <span className="rounded-full bg-red-400 px-1.5 py-0.5 text-[10px] text-slate-950">{pendingWithdrawals.length}</span>}
              </button>
            ))}
          </div>
        </div>

        <Tabs value={activeAdminTab} onValueChange={setActiveAdminTab} className="space-y-5">
          <TabsList className="hidden">
            <TabsTrigger value="home">Início</TabsTrigger><TabsTrigger value="users">Usuários</TabsTrigger>
            <TabsTrigger value="deposits">Depósitos</TabsTrigger><TabsTrigger value="withdrawals">Saques</TabsTrigger>
            <TabsTrigger value="trades">Negociações</TabsTrigger><TabsTrigger value="contracts">Contratos</TabsTrigger><TabsTrigger value="settings">Configurações</TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="mt-0 space-y-4">
            <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-4 shadow-2xl sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-400"><Activity className="h-4 w-4" /> Operação em tempo real</div>
                  <h2 className="mt-1 text-2xl font-black text-white">Visão geral COFY</h2>
                  <p className="mt-1 text-sm text-slate-400">Mercado, usuários, ordens e financeiro na mesma tela.</p>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/70 px-3 py-2 text-xs text-slate-400">
                  <span className={`h-2.5 w-2.5 rounded-full ${lastRefreshOk ? "animate-pulse bg-emerald-400" : "bg-red-400"}`} />
                  {autoRefresh ? `Atualiza a cada ${Math.round(refreshMs / 1000)}s` : "Atualização automática desligada"}
                </div>
              </div>
            </div>

            {dashboard ? (
              <>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <div className="rounded-3xl border border-blue-500/20 bg-blue-500/10 p-4"><Users className="mb-3 h-6 w-6 text-blue-300" /><div className="text-[10px] font-black uppercase tracking-wider text-blue-200/70">Usuários</div><div className="mt-1 text-3xl font-black text-white">{dashboard.total_users ?? 0}</div><div className="mt-1 text-xs text-slate-400">cadastrados</div></div>
                  <div className="rounded-3xl border border-cyan-500/20 bg-cyan-500/10 p-4"><CircleDollarSign className="mb-3 h-6 w-6 text-cyan-300" /><div className="text-[10px] font-black uppercase tracking-wider text-cyan-200/70">Depósitos</div><div className="mt-1 text-lg font-black text-white">{formatMoney(dashboard.total_deposit_amount)}</div><div className="mt-1 text-xs text-slate-400">confirmados</div></div>
                  <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4"><Wallet className="mb-3 h-6 w-6 text-emerald-300" /><div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Saldo carteiras</div><div className="mt-1 text-lg font-black text-white">{formatMoney(dashboard.brl_available)}</div><div className="mt-1 text-xs text-slate-500">saldo livre</div></div>
                  <div className={`rounded-3xl border p-4 ${n(dashboard.house_net_result) >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}><BadgeDollarSign className={`mb-3 h-6 w-6 ${n(dashboard.house_net_result) >= 0 ? "text-emerald-300" : "text-red-300"}`} /><div className="text-[10px] font-black uppercase tracking-wider text-slate-400">Resultado líquido COFY</div><div className={`mt-1 text-lg font-black ${n(dashboard.house_net_result) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{n(dashboard.house_net_result) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_net_result)))}</div><div className="mt-1 text-xs text-slate-500">realizado + aberto − parceiros</div></div>
                </div>

                <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
                  <div className="mb-4 flex items-center justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Trader aberto</div><h3 className="mt-1 text-lg font-black text-white">Operações em andamento</h3></div><div className="rounded-full border border-violet-500/25 bg-violet-500/10 px-3 py-1 text-xs font-black text-violet-300">{dashboard.open_orders_count || "—"} ordens</div></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-3xl border border-emerald-500/25 bg-gradient-to-b from-emerald-500/15 to-emerald-500/5 p-4"><div className="flex items-center justify-between"><div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-400 text-slate-950"><TrendingUp className="h-6 w-6" /></div><span className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Alta</span></div><div className="mt-4 text-sm font-black text-emerald-200">COMPRAS ABERTAS</div><div className="mt-1 text-2xl font-black text-white">{formatMoney(dashboard.open_buy_total)}</div><div className="mt-2 text-xs text-emerald-300/70">{dashboard.open_buy_count ? `${dashboard.open_buy_count} ordem(ns)` : "Operações de compra"}</div></div>
                    <div className="rounded-3xl border border-red-500/25 bg-gradient-to-b from-red-500/15 to-red-500/5 p-4"><div className="flex items-center justify-between"><div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-400 text-slate-950"><TrendingDown className="h-6 w-6" /></div><span className="text-[10px] font-black uppercase tracking-wider text-red-300">Baixa</span></div><div className="mt-4 text-sm font-black text-red-200">VENDAS ABERTAS</div><div className="mt-1 text-2xl font-black text-white">{formatMoney(dashboard.open_sell_total)}</div><div className="mt-2 text-xs text-red-300/70">{dashboard.open_sell_count ? `${dashboard.open_sell_count} ordem(ns)` : "Operações de venda"}</div></div>
                  </div>
                  <div className="mt-3 rounded-2xl border border-violet-500/20 bg-violet-500/5 p-4"><div className="flex items-center justify-between gap-3"><span className="text-xs font-bold uppercase tracking-wider text-violet-300">Total em ordens abertas</span><span className="text-xl font-black text-white">{formatMoney(dashboard.open_orders_total)}</span></div></div>
                </div>

                {/* Mesa ao vivo das posições abertas */}
                <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-violet-300">
                        <Activity className="h-4 w-4" />
                        Mesa ao vivo
                      </div>
                      <h3 className="mt-1 text-lg font-black text-white">Posições abertas agora</h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Entrada, preço atual, resultado do cliente e resultado líquido da COFY.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${dashboard.risk_market_ok ? "animate-pulse bg-emerald-400" : "bg-amber-400"}`} />
                      <span className="text-xs font-bold text-slate-400">
                        {dashboard.risk_market_ok ? "Preço atualizado" : "Aguardando mercado"}
                      </span>
                    </div>
                  </div>

                  {Array.isArray(dashboard.open_positions_live) && dashboard.open_positions_live.length > 0 ? (
                    <div className="mt-4 space-y-3">
                      {dashboard.open_positions_live.map((pos) => {
                        const isBuy = String(pos?.side || "").toLowerCase() === "buy";
                        const clientPl = n(pos?.client_pl);
                        const houseNet = n(pos?.house_net_pl);
                        const userLabel = pos?.user_name || pos?.user_email || `Usuário #${pos?.user_id ?? "—"}`;

                        return (
                          <div key={pos.id} className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/65">
                            <div className="flex flex-col gap-3 border-b border-slate-800 p-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-black ${isBuy ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                                    {isBuy ? "COMPRA • ALTA" : "VENDA • BAIXA"}
                                  </span>
                                  <span className="text-xs font-black text-white">Ordem #{pos.id}</span>
                                </div>
                                <div className="mt-2 truncate text-sm font-bold text-slate-200">{userLabel}</div>
                                <div className="mt-0.5 text-[10px] text-slate-600">
                                  Usuário #{pos?.user_id ?? "—"} • {formatDateTime(pos?.created_at)}
                                </div>
                              </div>

                              <div className="text-left sm:text-right">
                                <div className="text-[9px] font-black uppercase tracking-wider text-slate-600">Valor da ordem</div>
                                <div className="mt-1 text-xl font-black text-white">{formatMoney(pos?.amount)}</div>
                                <div className="text-[10px] text-slate-500">{formatNumber(pos?.qty_sacks, 4)} sacas</div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-px bg-slate-800 lg:grid-cols-4">
                              <div className="bg-slate-950/90 p-3">
                                <div className="text-[9px] font-black uppercase tracking-wider text-slate-600">Entrada</div>
                                <div className="mt-1 text-sm font-black text-white">{formatMoney(pos?.entry_price)}</div>
                              </div>
                              <div className="bg-slate-950/90 p-3">
                                <div className="text-[9px] font-black uppercase tracking-wider text-slate-600">Saída agora</div>
                                <div className="mt-1 text-sm font-black text-cyan-300">
                                  {pos?.current_exit_price == null ? "—" : formatMoney(pos.current_exit_price)}
                                </div>
                              </div>
                              <div className={`bg-slate-950/90 p-3 ${clientPl >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                <div className="text-[9px] font-black uppercase tracking-wider text-slate-600">P/L cliente</div>
                                <div className="mt-1 text-sm font-black">
                                  {clientPl >= 0 ? "+" : "-"} {formatMoney(Math.abs(clientPl))}
                                </div>
                              </div>
                              <div className={`bg-slate-950/90 p-3 ${houseNet >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                <div className="text-[9px] font-black uppercase tracking-wider text-slate-600">Líquido COFY</div>
                                <div className="mt-1 text-sm font-black">
                                  {houseNet >= 0 ? "+" : "-"} {formatMoney(Math.abs(houseNet))}
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[10px] text-slate-600">
                              <span>Spread: {formatNumber(pos?.spread_percentage, 4)}%</span>
                              <span>Bruto COFY: {formatMoney(pos?.house_gross_pl)}</span>
                              <span>Parceiro: - {formatMoney(pos?.partner_commission)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-6 text-center">
                      <Activity className="mx-auto h-7 w-7 text-slate-700" />
                      <div className="mt-2 text-sm font-bold text-slate-400">Nenhuma posição aberta</div>
                      <div className="mt-1 text-xs text-slate-600">Quando houver uma ordem aberta ela aparecerá aqui automaticamente.</div>
                    </div>
                  )}
                </div>

                {/* Centro de Risco COFY */}
                <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-400">Gestão de contraparte</div>
                      <h3 className="mt-1 text-lg font-black text-white">Risco e resultado COFY</h3>
                      <p className="mt-1 text-xs text-slate-500">Resultado realizado, marcação das abertas e exposição líquida.</p>
                    </div>
                    <div className={`w-fit rounded-full border px-3 py-1 text-[10px] font-black ${dashboard.risk_market_ok ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-amber-500/25 bg-amber-500/10 text-amber-300"}`}>
                      {dashboard.risk_market_ok ? "MTM ATUALIZADO" : "SEM PREÇO PARA MTM"}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <div className={`rounded-2xl border p-3 ${n(dashboard.house_open_mtm) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">P/L aberto COFY</div>
                      <div className={`mt-1 text-lg font-black ${n(dashboard.house_open_mtm) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {n(dashboard.house_open_mtm) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_open_mtm)))}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-600">se liquidasse agora</div>
                    </div>

                    <div className={`rounded-2xl border p-3 ${n(dashboard.house_realized_gross) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Realizado COFY</div>
                      <div className={`mt-1 text-lg font-black ${n(dashboard.house_realized_gross) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {n(dashboard.house_realized_gross) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_realized_gross)))}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-600">ordens encerradas</div>
                    </div>

                    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Spread gerado</div>
                      <div className="mt-1 text-lg font-black text-cyan-300">{formatMoney(dashboard.spread_revenue_total)}</div>
                      <div className="mt-1 text-[10px] text-slate-600">já embutido no P/L</div>
                    </div>

                    <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-3">
                      <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Comissões parceiros</div>
                      <div className="mt-1 text-lg font-black text-violet-300">- {formatMoney(dashboard.partner_commissions_total)}</div>
                      <div className="mt-1 text-[10px] text-slate-600">obrigação gerada</div>
                    </div>
                  </div>

                  <div className={`mt-3 rounded-2xl border p-4 ${n(dashboard.house_net_result) >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Resultado líquido da casa</div>
                    <div className={`mt-1 text-3xl font-black ${n(dashboard.house_net_result) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {n(dashboard.house_net_result) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_net_result)))}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <div className="rounded-2xl border border-emerald-500/15 bg-slate-950/60 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Comprados</div>
                      <div className="mt-1 text-base font-black text-white">{formatNumber(dashboard.open_buy_qty, 2)} sacas</div>
                      <div className="mt-1 text-[10px] text-emerald-300">{formatMoney(dashboard.open_buy_total)}</div>
                    </div>
                    <div className="rounded-2xl border border-red-500/15 bg-slate-950/60 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Vendidos</div>
                      <div className="mt-1 text-base font-black text-white">{formatNumber(dashboard.open_sell_qty, 2)} sacas</div>
                      <div className="mt-1 text-[10px] text-red-300">{formatMoney(dashboard.open_sell_total)}</div>
                    </div>
                    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Exposição líquida</div>
                      <div className="mt-1 text-base font-black text-amber-300">{formatNumber(Math.abs(n(dashboard.net_customer_qty)), 2)} sacas</div>
                      <div className="mt-1 text-[10px] text-slate-500">{formatMoney(dashboard.net_exposure_brl)}</div>
                    </div>
                    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">Hedge teórico</div>
                      <div className="mt-1 text-base font-black text-cyan-300">
                        {dashboard.hedge_side === "buy" ? "COMPRAR" : dashboard.hedge_side === "sell" ? "VENDER" : "NEUTRO"}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-500">{formatNumber(dashboard.hedge_qty_sacks, 2)} sacas</div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Stress sem hedge</div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div className="rounded-xl bg-slate-900 p-3 text-center"><div className="text-[9px] text-slate-500">1%</div><div className="mt-1 text-sm font-black text-amber-300">{formatMoney(dashboard.risk_shock_1pct_brl)}</div></div>
                      <div className="rounded-xl bg-slate-900 p-3 text-center"><div className="text-[9px] text-slate-500">5%</div><div className="mt-1 text-sm font-black text-orange-300">{formatMoney(dashboard.risk_shock_5pct_brl)}</div></div>
                      <div className="rounded-xl bg-slate-900 p-3 text-center"><div className="text-[9px] text-slate-500">10%</div><div className="mt-1 text-sm font-black text-red-300">{formatMoney(dashboard.risk_shock_10pct_brl)}</div></div>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
                  <div className="mb-4 flex items-center justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Mercado agora</div><h3 className="mt-1 text-lg font-black text-white">Café NY + Dólar</h3></div><div className={`rounded-full border px-3 py-1 text-xs font-black ${mktOk ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-red-500/25 bg-red-500/10 text-red-300"}`}>{mktOk ? "Feed ativo" : "Feed indisponível"}</div></div>
                  {mktOk ? <>
                    <div className="rounded-3xl border border-slate-800 bg-slate-950/65 p-4">
                      <div className="flex items-start justify-between gap-3"><div><div className="text-xs text-slate-500">Café NY • {market.contract || "—"}</div><div className="mt-1 text-4xl font-black tracking-tight text-white">{formatNumber(kc, 2)}<span className="ml-1 text-sm text-slate-500">¢/lb</span></div></div><div className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black ${marketChangePct > 0 ? "bg-emerald-500/15 text-emerald-300" : marketChangePct < 0 ? "bg-red-500/15 text-red-300" : "bg-slate-800 text-slate-300"}`}>{marketChangePct > 0 ? <TrendingUp className="h-4 w-4" /> : marketChangePct < 0 ? <TrendingDown className="h-4 w-4" /> : null}{marketChangePct > 0 ? "+" : ""}{formatNumber(marketChangePct, 2)}%</div></div>
                      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-800 pt-3 text-xs"><div className="flex justify-between gap-2"><span className="text-slate-500">Abertura</span><strong className="text-slate-200">{formatNumber(marketOpen, 2)}</strong></div><div className="flex justify-between gap-2"><span className="text-slate-500">Máxima</span><strong className="text-emerald-300">{formatNumber(marketHigh, 2)}</strong></div><div className="flex justify-between gap-2"><span className="text-slate-500">Mínima</span><strong className="text-red-300">{formatNumber(marketLow, 2)}</strong></div><div className="flex justify-between gap-2"><span className="text-slate-500">Fechamento</span><strong className="text-slate-200">{formatNumber(marketClose, 2)}</strong></div></div>
                      <div className="mt-3 grid grid-cols-2 gap-2"><div className="rounded-2xl border border-emerald-500/15 bg-emerald-500/5 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500">Bid</div><div className="mt-1 text-lg font-black text-emerald-300">{formatNumber(marketBid, 2)}</div></div><div className="rounded-2xl border border-red-500/15 bg-red-500/5 p-3 text-right"><div className="text-[10px] uppercase tracking-wider text-slate-500">Ask</div><div className="mt-1 text-lg font-black text-red-300">{formatNumber(marketAsk, 2)}</div></div></div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3"><div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-4"><div className="text-[10px] font-black uppercase tracking-wider text-cyan-200/70">Dólar USDT/BRL</div><div className="mt-1 text-2xl font-black text-white">R$ {formatNumber(fx, 4)}</div><div className="mt-1 text-[11px] text-slate-500">Cache {cacheFX == null ? "n/a" : `${cacheFX}s`}</div></div><div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4"><div className="text-[10px] font-black uppercase tracking-wider text-emerald-200/70">Saca referência</div><div className="mt-1 text-2xl font-black text-emerald-300">{formatMoney(saca)}</div><div className="mt-1 text-[11px] text-slate-500">KC + dólar</div></div></div>
                  </> : <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">Sem feed válido no momento.</div>}
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className={`rounded-3xl border p-4 sm:p-5 ${pendingWithdrawals.length > 0 ? "border-red-500/25 bg-red-500/5" : "border-slate-800 bg-slate-900"}`}>
                    <div className="flex items-center justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Saques</div><h3 className="mt-1 text-lg font-black text-white">Pendentes</h3></div><div className={`rounded-full px-3 py-1 text-xs font-black ${pendingWithdrawals.length > 0 ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}>{pendingWithdrawals.length}</div></div>
                    <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-950/60 p-3"><div className="text-xs text-slate-500">Valor aguardando</div><div className="mt-1 text-2xl font-black text-white">{formatMoney(pendingWithdrawalAmount)}</div></div>
                    {pendingWithdrawals.length > 0 ? <div className="mt-3 space-y-2">{pendingWithdrawals.slice(0,5).map(w => <div key={w.id} className="rounded-2xl border border-slate-800 bg-slate-950/55 p-3"><div className="flex items-start justify-between gap-3"><div><div className="text-xs font-bold text-white">Saque #{w.id} • Usuário #{w.user_id}</div><div className="mt-1 text-lg font-black text-red-300">{formatMoney(w.amount)}</div></div><Clock3 className="h-4 w-4 text-amber-300" /></div><div className="mt-2 break-all text-[11px] text-slate-500">PIX: {w.pix_key || "—"}</div><div className="mt-1 text-[11px] text-slate-600">{formatDateTime(w.created_at)}</div></div>)}</div> : <div className="mt-3 text-sm text-slate-500">Nenhum saque aguardando processamento.</div>}
                  </div>

                  <div className={`rounded-3xl border p-4 sm:p-5 ${pendingDeposits.length > 0 ? "border-amber-500/25 bg-amber-500/5" : "border-slate-800 bg-slate-900"}`}>
                    <div className="flex items-center justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Depósitos</div><h3 className="mt-1 text-lg font-black text-white">Pendentes</h3></div><div className={`rounded-full px-3 py-1 text-xs font-black ${pendingDeposits.length > 0 ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{pendingDeposits.length}</div></div>
                    <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-950/60 p-3"><div className="text-xs text-slate-500">Valor aguardando</div><div className="mt-1 text-2xl font-black text-white">{formatMoney(pendingDepositAmount)}</div></div>
                    {pendingDeposits.length > 0 ? <div className="mt-3 space-y-2">{pendingDeposits.slice(0,5).map(d => <div key={d.id} className="rounded-2xl border border-slate-800 bg-slate-950/55 p-3"><div className="flex items-start justify-between gap-3"><div><div className="text-xs font-bold text-white">Depósito #{d.id} • Usuário #{d.user_id}</div><div className="mt-1 text-lg font-black text-amber-300">{formatMoney(d.amount)}</div></div><Clock3 className="h-4 w-4 text-amber-300" /></div><div className="mt-1 text-[11px] text-slate-600">Status: {d.status || "pending"}</div></div>)}</div> : <div className="mt-3 text-sm text-slate-500">Nenhum depósito aguardando confirmação.</div>}
                  </div>
                </div>

                {(dashboard.house_profit_today !== null || dashboard.portfolio_total !== null) && <div className="grid gap-3 sm:grid-cols-2">{dashboard.house_profit_today !== null && <div className={`rounded-3xl border p-5 ${n(dashboard.house_profit_today) >= 0 ? "border-emerald-500/25 bg-emerald-500/10" : "border-red-500/25 bg-red-500/10"}`}><div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Resultado da casa hoje</div><div className={`mt-2 text-3xl font-black ${n(dashboard.house_profit_today) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{n(dashboard.house_profit_today) >= 0 ? "+" : "-"} {formatMoney(Math.abs(n(dashboard.house_profit_today)))}</div></div>}{dashboard.portfolio_total !== null && <div className="rounded-3xl border border-violet-500/20 bg-violet-500/10 p-5"><div className="text-[10px] font-black uppercase tracking-widest text-violet-300">Patrimônio dos usuários</div><div className="mt-2 text-3xl font-black text-white">{formatMoney(dashboard.portfolio_total)}</div></div>}</div>}
              </>
            ) : <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400">Carregando indicadores...</div>}
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            {settings && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Configurações da Plataforma</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-white">Asaas PIX</h3>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">API Key</Label>
                        <Input
                          type="password"
                          placeholder="Asaas API Key"
                          value={settings.asaas_api_key || ""}
                          onChange={(e) => setSettings({ ...settings, asaas_api_key: e.target.value })}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Ambiente</Label>
                        <select
                          value={settings.asaas_environment}
                          onChange={(e) => setSettings({ ...settings, asaas_environment: e.target.value })}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                        >
                          <option value="sandbox">Sandbox</option>
                          <option value="production">Produção</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-slate-800">
                    <h3 className="text-lg font-semibold text-white">Google OAuth</h3>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Client ID</Label>
                        <Input
                          placeholder="Google Client ID"
                          value={settings.google_client_id || ""}
                          onChange={(e) => setSettings({ ...settings, google_client_id: e.target.value })}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Client Secret</Label>
                        <Input
                          type="password"
                          placeholder="Google Client Secret"
                          value={settings.google_client_secret || ""}
                          onChange={(e) => setSettings({ ...settings, google_client_secret: e.target.value })}
                          className="bg-slate-800 border-slate-700 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveSettings}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    Salvar Configurações
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contracts */}
          <TabsContent value="contracts">
            <div className="space-y-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Criar Novo Contrato</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-4">
                    <Input
                      placeholder="Símbolo (ex: KCZ5)"
                      value={newContract.symbol}
                      onChange={(e) => setNewContract({ ...newContract, symbol: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <Input
                      placeholder="Nome"
                      value={newContract.name}
                      onChange={(e) => setNewContract({ ...newContract, name: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="Spread %"
                      value={newContract.spread}
                      onChange={(e) => setNewContract({ ...newContract, spread: parseFloat(e.target.value) })}
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                    <Button onClick={handleCreateContract} className="bg-gradient-to-r from-emerald-500 to-cyan-500">
                      <Plus className="w-4 h-4 mr-2" />
                      Criar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Contratos Cadastrados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {contracts.map((contract) => (
                      <div key={contract.id} className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                        {editingContract?.id === contract.id ? (
                          <div className="flex-1 grid md:grid-cols-4 gap-4">
                            <Input
                              value={editingContract.symbol}
                              onChange={(e) => setEditingContract({ ...editingContract, symbol: e.target.value })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <Input
                              value={editingContract.name}
                              onChange={(e) => setEditingContract({ ...editingContract, name: e.target.value })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <Input
                              type="number"
                              step="0.1"
                              value={editingContract.spread}
                              onChange={(e) => setEditingContract({ ...editingContract, spread: parseFloat(e.target.value) })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleUpdateContract(editingContract)} className="bg-emerald-500 hover:bg-emerald-600">
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingContract(null)} className="border-slate-700">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex-1 grid md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-sm text-slate-400">Símbolo</div>
                                <div className="text-white font-semibold">{contract.symbol}</div>
                              </div>
                              <div>
                                <div className="text-sm text-slate-400">Nome</div>
                                <div className="text-white">{contract.name}</div>
                              </div>
                              <div>
                                <div className="text-sm text-slate-400">Spread</div>
                                <div className="text-white">{contract.spread}%</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={!!contract.active}
                                onCheckedChange={(checked) => handleUpdateContract({ ...contract, active: checked })}
                              />
                              <Button size="sm" variant="outline" onClick={() => setEditingContract(contract)} className="border-slate-700">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteContract(contract.id)}
                                className="border-slate-700 text-red-400 hover:text-red-300"
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Deposits */}
          <TabsContent value="deposits">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Depósitos Pendentes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pendingDeposits.map((deposit) => (
                    <div key={deposit.id} className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                      <div className="flex-1 grid md:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-slate-400">Usuário</div>
                          <div className="text-white">{deposit.user_id}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Valor</div>
                          <div className="text-white font-semibold">{formatMoney(deposit.amount)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Código PIX</div>
                          <div className="text-white font-mono text-sm break-all">{deposit.pix_code}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Status</div>
                          <div className="text-yellow-400">{deposit.status}</div>
                        </div>
                      </div>
                      <Button onClick={() => handleConfirmDeposit(deposit.id)} className="bg-emerald-500 hover:bg-emerald-600">
                        Confirmar
                      </Button>
                    </div>
                  ))}
                  {pendingDeposits.length === 0 && (
                    <div className="text-center text-slate-400 py-8">Nenhum depósito pendente</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals */}
          <TabsContent value="withdrawals">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Saques</span>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Button variant={withdrawStatus === "pending" ? "default" : "outline"} onClick={() => fetchWithdrawals("pending")}>
                      Pendentes
                    </Button>
                    <Button variant={withdrawStatus === "paid" ? "default" : "outline"} onClick={() => fetchWithdrawals("paid")}>
                      Pagos
                    </Button>
                    <Button variant={withdrawStatus === "rejected" ? "default" : "outline"} onClick={() => fetchWithdrawals("rejected")}>
                      Recusados
                    </Button>
                    <Button variant={withdrawStatus === "all" ? "default" : "outline"} onClick={() => fetchWithdrawals("all")}>
                      Todos
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(withdrawals || []).map((w) => (
                    <div key={w.id} className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                      <div className="flex-1 grid md:grid-cols-5 gap-4">
                        <div>
                          <div className="text-sm text-slate-400">ID</div>
                          <div className="text-white">{w.id}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Usuário</div>
                          <div className="text-white">{w.user_id}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Valor</div>
                          <div className="text-white font-semibold">{formatMoney(w.amount)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">PIX</div>
                          <div className="text-white text-sm break-all">{w.pix_key || "—"}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Criado</div>
                          <div className="text-white text-sm">{formatDateTime(w.created_at)}</div>
                        </div>
                      </div>

                      {w.status === "pending" ? (
                        <div className="flex gap-2">
                          <Button onClick={() => handleConfirmWithdrawal(w.id)} className="bg-emerald-500 hover:bg-emerald-600">
                            <Check className="w-4 h-4 mr-2" />
                            Confirmar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleRejectWithdrawal(w.id)}
                            className="border-slate-700 text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Recusar
                          </Button>
                        </div>
                      ) : (
                        <div className="text-slate-400 text-sm">
                          Status: <span className="text-white">{w.status}</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {(!withdrawals || withdrawals.length === 0) && (
                    <div className="text-center text-slate-400 py-8">Nenhum saque encontrado</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Histórico de Negociações */}
          <TabsContent value="trades" className="mt-0 space-y-4">
            <div className="rounded-3xl border border-slate-800 bg-slate-900 p-4 sm:p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400">Auditoria</div>
                  <h2 className="mt-1 text-xl font-black text-white">Histórico de negociações</h2>
                  <p className="mt-1 text-xs text-slate-500">P/L do cliente e espelho da COFY operação por operação.</p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  disabled={tradesLoading}
                  onClick={() => fetchTrades({ page: tradesPage })}
                  className="border-slate-700"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${tradesLoading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                <select
                  value={tradesPeriod}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradesPeriod(value);
                    setTradesPage(1);
                    fetchTrades({ page: 1, period: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white"
                >
                  <option value="today">Hoje</option>
                  <option value="7d">Últimos 7 dias</option>
                  <option value="30d">Últimos 30 dias</option>
                  <option value="all">Todo período</option>
                </select>

                <select
                  value={tradesStatus}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradesStatus(value);
                    setTradesPage(1);
                    fetchTrades({ page: 1, status: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white"
                >
                  <option value="all">Todos os status</option>
                  <option value="open">Abertas</option>
                  <option value="closed">Fechadas</option>
                  <option value="canceled">Canceladas</option>
                </select>

                <select
                  value={tradesSide}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTradesSide(value);
                    setTradesPage(1);
                    fetchTrades({ page: 1, side: value });
                  }}
                  className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-white"
                >
                  <option value="all">Compra e venda</option>
                  <option value="buy">Compras</option>
                  <option value="sell">Vendas</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Volume</div>
                <div className="mt-1 text-base font-black text-white">{formatMoney(tradesSummary.volume)}</div>
              </div>
              <div className={`rounded-2xl border p-3 ${n(tradesSummary.client_pl) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">P/L clientes</div>
                <div className={`mt-1 text-base font-black ${n(tradesSummary.client_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(tradesSummary.client_pl)}</div>
              </div>
              <div className={`rounded-2xl border p-3 ${n(tradesSummary.house_gross_pl) >= 0 ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">COFY bruto</div>
                <div className={`mt-1 text-base font-black ${n(tradesSummary.house_gross_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(tradesSummary.house_gross_pl)}</div>
              </div>
              <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Parceiros</div>
                <div className="mt-1 text-base font-black text-violet-300">- {formatMoney(tradesSummary.partner_commission)}</div>
              </div>
              <div className={`col-span-2 rounded-2xl border p-3 lg:col-span-1 ${n(tradesSummary.house_net_pl) >= 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">COFY líquido</div>
                <div className={`mt-1 text-xl font-black ${n(tradesSummary.house_net_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(tradesSummary.house_net_pl)}</div>
              </div>
            </div>

            <div className="space-y-2">
              {tradesLoading && trades.length === 0 ? (
                <div className="rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400">
                  <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Carregando negociações...
                </div>
              ) : trades.length === 0 ? (
                <div className="rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400">
                  Nenhuma negociação encontrada neste filtro.
                </div>
              ) : (
                trades.map((trade) => (
                  <div key={trade.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-lg px-2 py-1 text-[10px] font-black ${trade.side === "buy" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                            {trade.side === "buy" ? "COMPRA" : "VENDA"}
                          </span>
                          <span className="text-sm font-black text-white">Ordem #{trade.id}</span>
                          <span className="rounded-lg bg-slate-800 px-2 py-1 text-[10px] font-bold text-slate-400">
                            {trade.status === "open" ? "ABERTA" : trade.status === "closed" ? "FECHADA" : "CANCELADA"}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-slate-400">
                          {trade.user_name || trade.user_email || `Usuário #${trade.user_id}`}
                        </div>
                        <div className="mt-1 text-[10px] text-slate-600">
                          Aberta: {formatDateTime(trade.created_at)}
                          {trade.closed_at ? ` • Fechada: ${formatDateTime(trade.closed_at)}` : ""}
                        </div>
                      </div>

                      <div className={`text-left sm:text-right ${n(trade.house_net_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Resultado líquido COFY</div>
                        <div className="mt-1 text-xl font-black">{formatMoney(trade.house_net_pl)}</div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">Valor</div><div className="mt-1 text-xs font-bold text-white">{formatMoney(trade.amount)}</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">Sacas</div><div className="mt-1 text-xs font-bold text-white">{formatNumber(trade.qty_sacks, 4)}</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">Entrada</div><div className="mt-1 text-xs font-bold text-white">{formatMoney(trade.entry_price)}</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">{trade.status === "open" ? "Saída agora" : "Saída"}</div><div className="mt-1 text-xs font-bold text-white">{trade.status === "open" ? formatMoney(trade.current_exit_price) : formatMoney(trade.close_price)}</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">Spread</div><div className="mt-1 text-xs font-bold text-cyan-300">{formatNumber(trade.spread_percentage, 4)}%</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">P/L cliente</div><div className={`mt-1 text-xs font-bold ${n(trade.client_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(trade.client_pl)}</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">COFY bruto</div><div className={`mt-1 text-xs font-bold ${n(trade.house_gross_pl) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{formatMoney(trade.house_gross_pl)}</div></div>
                      <div className="rounded-xl bg-slate-950/60 p-2.5"><div className="text-[9px] text-slate-600">Comissão</div><div className="mt-1 text-xs font-bold text-violet-300">{formatMoney(trade.partner_commission)}</div></div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={tradesLoading || tradesPage <= 1}
                onClick={() => {
                  const next = tradesPage - 1;
                  setTradesPage(next);
                  fetchTrades({ page: next });
                }}
                className="border-slate-700"
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Anterior
              </Button>

              <div className="text-xs text-slate-500">Página {tradesPage} de {tradesTotalPages}</div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={tradesLoading || tradesPage >= tradesTotalPages}
                onClick={() => {
                  const next = tradesPage + 1;
                  setTradesPage(next);
                  fetchTrades({ page: next });
                }}
                className="border-slate-700"
              >
                Próxima <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="text-white">Usuários Cadastrados</CardTitle>
                    <p className="text-xs text-slate-400 mt-1">
                      {usersTotal} usuário(s) encontrado(s)
                    </p>
                  </div>
                </div>

                <form onSubmit={handleUsersSearch} className="mt-3">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        value={usersSearchInput}
                        onChange={(e) => setUsersSearchInput(e.target.value)}
                        placeholder="Buscar nome, e-mail, CPF ou telefone"
                        className="pl-9 bg-slate-950 border-slate-700 text-white"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={usersLoading}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Buscar
                    </Button>

                    {(usersQuery || usersSearchInput) && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleUsersClearSearch}
                        disabled={usersLoading}
                        className="border-slate-700"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Limpar
                      </Button>
                    )}
                  </div>
                </form>
              </CardHeader>

              <CardContent>
                {usersLoading ? (
                  <div className="py-8 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Carregando usuários...
                  </div>
                ) : adminUsers.length === 0 ? (
                  <div className="py-8 text-center text-slate-400">
                    Nenhum usuário encontrado.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adminUsers.map((u) => {
                      const isOpen = expandedUserId === u.id;

                      return (
                        <div
                          key={u.id}
                          className="rounded-xl border border-slate-800 bg-slate-950/50 overflow-hidden"
                        >
                          {/* Linha compacta */}
                          <div className="p-3">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="font-semibold text-white truncate">
                                    {u.name || u.email || `Usuário #${u.id}`}
                                  </div>

                                  {u.role === "admin" && (
                                    <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[10px]">
                                      Admin
                                    </span>
                                  )}
                                </div>

                                <div className="text-xs text-slate-400 truncate mt-0.5">
                                  {u.email || "Sem e-mail"}
                                </div>

                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                                  <span>
                                    Saldo:{" "}
                                    <strong className="text-white">
                                      {formatMoney(u.balance)}
                                    </strong>
                                  </span>

                                  <span>
                                    Spread:{" "}
                                    <strong className="text-white">
                                      {u.spread_percentage === null ||
                                      u.spread_percentage === undefined
                                        ? "Padrão"
                                        : `${formatNumber(u.spread_percentage, 2)}%`}
                                    </strong>
                                  </span>

                                  {u.cpf && (
                                    <span className="hidden sm:inline">
                                      CPF: <strong className="text-slate-300">{u.cpf}</strong>
                                    </span>
                                  )}
                                </div>
                              </div>

                              <Button
                                type="button"
                                size="sm"
                                variant={isOpen ? "outline" : "default"}
                                onClick={() =>
                                  setExpandedUserId((current) =>
                                    current === u.id ? null : u.id
                                  )
                                }
                                className={
                                  isOpen
                                    ? "border-slate-700 w-full sm:w-auto"
                                    : "bg-emerald-500 hover:bg-emerald-600 w-full sm:w-auto"
                                }
                              >
                                {isOpen ? "Fechar" : "Abrir"}
                              </Button>
                            </div>
                          </div>

                          {/* Ficha completa: somente uma aberta por vez */}
                          {isOpen && (
                            <div className="border-t border-slate-800 p-2 sm:p-3">
                              <UserConfigCard
                                user={u}
                                onUpdate={async () => {
                                  await fetchDashboard();
                                  await fetchAdminUsers({
                                    page: usersPage,
                                    q: usersQuery,
                                  });
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Paginação */}
                <div className="mt-4 pt-4 border-t border-slate-800 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-slate-400 text-center sm:text-left">
                    Página {usersPage} de {usersTotalPages}
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={usersLoading || usersPage <= 1}
                      onClick={() => goToUsersPage(usersPage - 1)}
                      className="border-slate-700"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={usersLoading || usersPage >= usersTotalPages}
                      onClick={() => goToUsersPage(usersPage + 1)}
                      className="border-slate-700"
                    >
                      Próxima
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}